import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  listAdminTatlStories,
  deleteAdminTatlStory,
} from "../../lib/adminApi";

const AdminTatlStories = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | published | draft
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const isPublished =
        filter === "published" ? true : filter === "draft" ? false : undefined;
      const data = await listAdminTatlStories(isPublished);
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = `${r.title} ${r.child_name} ${r.location} ${r.excerpt}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  const onDelete = async (id) => {
    const ok = window.confirm("Delete this story permanently?");
    if (!ok) return;
    await deleteAdminTatlStory(id);
    await load();
  };

  return (
    <AdminLayout title="TATL1000 Stories" onRefresh={load}>
      <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            className="rounded-xl"
            onClick={() => setFilter("all")}
          >
            All
          </Button>
          <Button
            variant={filter === "published" ? "default" : "outline"}
            className="rounded-xl"
            onClick={() => setFilter("published")}
          >
            Published
          </Button>
          <Button
            variant={filter === "draft" ? "default" : "outline"}
            className="rounded-xl"
            onClick={() => setFilter("draft")}
          >
            Drafts
          </Button>
        </div>

        <div className="flex gap-2 items-center">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search stories..."
            className="w-64 max-w-full rounded-xl border border-stone-200 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
          />
          <Button asChild className="bg-amber-700 hover:bg-amber-800 rounded-xl">
            <Link to="/admin/tatl/new">Create Story</Link>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-stone-200 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl border-stone-200">
          <CardContent className="p-6">
            <p className="text-stone-700">No stories yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <Card key={s.id} className="rounded-2xl border-stone-200 shadow-sm">
              <CardContent className="p-5">
                <p className="text-xs text-stone-500">
                  {s.is_published ? "Published" : "Draft"}
                </p>
                <h3 className="font-semibold text-stone-900 mt-1 line-clamp-2">
                  {s.child_name}: {s.title}
                </h3>
                <p className="text-sm text-stone-600 mt-2 line-clamp-2">
                  {s.excerpt}
                </p>
                <p className="text-xs text-stone-500 mt-3">{s.location}</p>

                <div className="mt-4 flex gap-2">
                  <Button asChild variant="outline" className="rounded-xl">
                    <Link to={`/admin/tatl/${s.id}/edit`}>Edit</Link>
                  </Button>
                  <Button
                    variant="destructive"
                    className="rounded-xl"
                    onClick={() => onDelete(s.id)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTatlStories;
